import React, { useEffect, useState, useRef } from 'react';
import { CrowdProvider, useCrowd } from './context/CrowdContext';
import baseapi, { API_BASE } from '../baseapi';
import { LiveFrame } from '../components/junctions/LiveFrame';
import { readLast, saveLast } from '../utils/lastAnswer';
import {
  Camera,
  Video,
  Eye,
  Sliders,
  Sparkles,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Layers,
  Activity,
  Users,
  Zap,
  Clock,
  X,
  Pencil,
  Plus,
  Trash2,
  RotateCw
} from 'lucide-react';

interface TempleCamera {
  id: string;
  name: string;
  te: string;
  x: number;
  y: number;
  angle: number; // FOV direction in degrees (0 = right, 90 = down, 180 = left, 270 = up)
  fov: number; // FOV spread angle in degrees
  distanceM: number; // distance marker along queue in meters
  line: string;
  lineColor: string;
  sector: string;
  resolution: string;
  fps: number;
  defaultDens: number;
  kind?: 'queue' | 'exit' | 'area' | 'holding';
  /** id of the real camera at this spot (GET /api/cameras/overview). Without it the marker is only a planned position. */
  real?: string;
}

/** One row of GET /api/cameras/overview, as far as this page uses it. */
interface LiveCam { id: string; name: string; ip: string | null; state: string; tracks: number | null; today?: { people_toward: number; people_away: number } }

const fmt = (n: number | null | undefined) => (n == null ? '—' : Math.round(n).toLocaleString('en-IN'));
const GREY = '#94A3B8';

// 22 camera positions on crowd_management_replicated.svg (viewBox 0 0 930 1279). `real` binds a position to a real
// camera: its name, state, head count and video then come from the API. Positions without `real` are planned
// spots with no camera assigned yet: they are drawn grey and carry no figures.
// Bound on 11 Oct 2026: 192.168.2.41 and .38 (queue dividing point), .45 / .43 / .46 / .190 (Antharalayam entries,
// around the Darshan area), .44 and .125 (exit), 192.168.0.25 (CAM-TR-20).
const TEMPLE_CAMERAS: TempleCamera[] = [
  { id: 'CAM-TR-01', name: 'General Q-Lines Dividing Point', te: 'క్యూ విభజన పాయింట్', x: 105, y: 460, angle: 90, fov: 65, distanceM: 0, line: 'All Lines', lineColor: '#FF6600', sector: 'S-7', resolution: '4K UltraHD', fps: 60, defaultDens: 1.8, kind: 'queue', real: 'CAM-2-41' },
  { id: 'CAM-TR-02', name: 'West Queue Mid Section', te: 'పశ్చిమ క్యూ మార్గం - మధ్య', x: 105, y: 520, angle: 90, fov: 70, distanceM: 30, line: 'Free Lines', lineColor: '#0038FF', sector: 'S-7', resolution: '4K AI', fps: 60, defaultDens: 1.6, kind: 'queue', real: 'CAM-2-38' },
  { id: 'CAM-TR-03', name: 'West Queue North Junction', te: 'పశ్చిమ క్యూ - ఉత్తర మలుపు', x: 168, y: 180, angle: 270, fov: 70, distanceM: 55, line: 'Free Lines', lineColor: '#009E38', sector: 'S-8', resolution: '4K AI', fps: 60, defaultDens: 2.1, kind: 'queue' },
  { id: 'CAM-TR-04', name: 'North-West Corner Turn', te: 'వాయువ్య మూల మలుపు', x: 210, y: 80, angle: 0, fov: 75, distanceM: 80, line: 'All Free Lines', lineColor: '#FF6600', sector: 'S-8', resolution: '4K AI', fps: 60, defaultDens: 2.4, kind: 'queue' },
  { id: 'CAM-TR-05', name: 'North Free & Service Corridor', te: 'ఉత్తర క్యూ కారిడార్ - పశ్చిమ', x: 350, y: 80, angle: 0, fov: 70, distanceM: 110, line: 'Service & Free', lineColor: '#4C0070', sector: 'S-8', resolution: '4K AI', fps: 60, defaultDens: 1.7, kind: 'queue' },
  { id: 'CAM-TR-06', name: 'North Central Corridor Junction', te: 'ఉత్తర క్యూ కారిడార్ - మధ్య', x: 480, y: 80, angle: 0, fov: 70, distanceM: 145, line: 'Free Lines', lineColor: '#0038FF', sector: 'S-8', resolution: '4K AI', fps: 60, defaultDens: 2.0, kind: 'queue' },
  { id: 'CAM-TR-07', name: 'North-East Corner Main Turn', te: 'ఈశాన్య ప్రధాన మలుపు', x: 650, y: 80, angle: 0, fov: 70, distanceM: 175, line: 'Free Lines', lineColor: '#0038FF', sector: 'S-8', resolution: '4K AI', fps: 60, defaultDens: 2.3, kind: 'queue' },
  { id: 'CAM-TR-08', name: 'East Outer Corridor Approach', te: 'తూర్పు క్యూ మార్గం', x: 708, y: 200, angle: 90, fov: 65, distanceM: 200, line: 'Orange & Service', lineColor: '#FF6600', sector: 'S-9', resolution: '4K AI', fps: 60, defaultDens: 1.9, kind: 'queue' },
  { id: 'CAM-TR-09', name: 'East Serpent S-Bend Entry', te: 'సర్పాకార క్యూ ప్రవేశం', x: 708, y: 380, angle: 90, fov: 70, distanceM: 230, line: 'Orange Free Line', lineColor: '#FF6600', sector: 'S-9', resolution: '4K AI', fps: 60, defaultDens: 2.6, kind: 'queue' },
  { id: 'CAM-TR-10', name: 'East Serpent S-Bend Loop Point', te: 'సర్పాకార క్యూ మలుపు', x: 560, y: 510, angle: 0, fov: 75, distanceM: 260, line: 'Orange Free Line', lineColor: '#FF6600', sector: 'S-9', resolution: '4K AI', fps: 60, defaultDens: 2.7, kind: 'queue', real: 'CAM-3-95' },
  { id: 'CAM-TR-11', name: 'Dhwaja Stambha Queue Junction', te: 'ధ్వజస్తంభం క్యూ కూడలి', x: 585, y: 440, angle: 90, fov: 70, distanceM: 210, line: 'Blue/Green Free Lines', lineColor: '#009E38', sector: 'S-9', resolution: '4K UltraHD', fps: 60, defaultDens: 2.0, kind: 'queue', real: 'CAM-3-96' },
  { id: 'CAM-TR-12', name: 'Red Line North Bypass', te: 'ఎరుపు క్యూ - ఉత్తర ద్వార మార్గం', x: 512, y: 200, angle: 90, fov: 65, distanceM: 125, line: 'Red Free Line', lineColor: '#EE0000', sector: 'S-9', resolution: '4K AI', fps: 60, defaultDens: 1.5, kind: 'queue' },
  { id: 'CAM-TR-13', name: 'Red Line NW Gate Entry', te: 'ఎరుపు క్యూ - వాయువ్య ద్వారం', x: 300, y: 280, angle: 180, fov: 70, distanceM: 160, line: 'Red Free Line', lineColor: '#EE0000', sector: 'S-9', resolution: '4K AI', fps: 60, defaultDens: 2.2, kind: 'queue' },
  { id: 'CAM-TR-14', name: 'Garbhagriha Inner Sanctum (Sri Kanaka Durga)', te: 'శ్రీ కనకదుర్గ అమ్మవారి గర్భాలయం', x: 410, y: 450, angle: 90, fov: 110, distanceM: 190, line: 'Sanctum Sanctorum', lineColor: '#F59E0B', sector: 'S-9', resolution: '4K NightVision/AI', fps: 60, defaultDens: 1.3, kind: 'area', real: 'CAM-3-98' },
  { id: 'CAM-TR-15', name: 'Aseervachana Mandapam Blessing Hall', te: 'ఆశీర్వచన మండపం దర్శన కేంద్రం', x: 410, y: 620, angle: 270, fov: 100, distanceM: 220, line: 'Mandapam Queue Merged', lineColor: '#F59E0B', sector: 'S-9', resolution: '4K 360 AI', fps: 60, defaultDens: 2.5, kind: 'area', real: 'CAM-3-94' },
  { id: 'CAM-TR-16', name: 'Main Sanctum South Exit Corridor', te: 'గర్భాలయ దక్షిణ నిష్క్రమణ మార్గం', x: 485, y: 920, angle: 90, fov: 75, distanceM: 260, line: 'Sanctum South Exit', lineColor: '#00F0B5', sector: 'S-10', resolution: '4K AI', fps: 60, defaultDens: 1.6, kind: 'area' },
  { id: 'CAM-TR-17', name: 'NW Gate Checkpoint', te: 'వాయువ్య ద్వారం (NW Gate)', x: 260, y: 310, angle: 0, fov: 75, distanceM: 95, line: 'NW Gate Passage', lineColor: '#00F0B5', sector: 'S-9', resolution: '4K AI', fps: 60, defaultDens: 1.2, kind: 'area' },
  { id: 'CAM-TR-18', name: 'SW Gate Checkpoint', te: 'నైరుతి ద్వారం (SW Gate)', x: 260, y: 560, angle: 0, fov: 75, distanceM: 105, line: 'SW Gate Passage', lineColor: '#00F0B5', sector: 'S-9', resolution: '4K AI', fps: 60, defaultDens: 1.4, kind: 'area' },
  { id: 'CAM-TR-19', name: 'VVIP & VIP Steps Entry', te: 'వీవీఐపీ / వీఐపీ మెట్లు', x: 250, y: 855, angle: 0, fov: 80, distanceM: 0, line: 'VIP/VVIP Access', lineColor: '#A855F7', sector: 'S-7', resolution: '4K AI', fps: 60, defaultDens: 0.9, kind: 'area' },
  { id: 'CAM-TR-20', name: 'Old Steps Concourse Entry', te: 'పాత మెట్లు మార్గం', x: 250, y: 1040, angle: 0, fov: 80, distanceM: 0, line: 'Old Steps Entry', lineColor: '#F59E0B', sector: 'S-7', resolution: '4K AI', fps: 60, defaultDens: 2.1, kind: 'area', real: 'CAM-0-25' },
  { id: 'CAM-TR-21', name: 'Southern Side Front Gate Exit Concourse', te: 'దక్షిణ ముఖ ద్వారం (Front Gate)', x: 800, y: 690, angle: 0, fov: 75, distanceM: 290, line: 'South Front Exit', lineColor: '#06B6D4', sector: 'S-10', resolution: '4K AI', fps: 60, defaultDens: 1.7, kind: 'area', real: 'CAM-2-125' },
  { id: 'CAM-TR-22', name: 'Temple Main Dispersal Gate (EXIT)', te: 'ప్రధాన ఆలయ నిష్క్రమణ ద్వారం (EXIT)', x: 800, y: 750, angle: 0, fov: 80, distanceM: 320, line: 'South Main Exit Gate', lineColor: '#10B981', sector: 'S-10', resolution: '4K AI', fps: 60, defaultDens: 1.8, kind: 'exit', real: 'CAM-2-44' }
];

/** The saved arrangement (GET /api/templeroom/layout "positions") is kept in this browser under this key. */
const LAYOUT_KEY = 'templeroom-layout';
/** One position of a saved arrangement. */
interface SavedPos { id: string; x: number; y: number; angle?: number; camera?: string | null; removed?: boolean }

/** The built-in positions changed by a saved arrangement: moved, turned, rebound, removed or added positions. */
const applySaved = (saved: SavedPos[]): TempleCamera[] => {
  if (!Array.isArray(saved) || !saved.length) return TEMPLE_CAMERAS;
  const by: Record<string, SavedPos> = Object.fromEntries(saved.map(p => [p.id, p]));
  const base = TEMPLE_CAMERAS.filter(c => !by[c.id]?.removed)
    .map(c => (by[c.id] ? { ...c, x: by[c.id].x, y: by[c.id].y, angle: by[c.id].angle ?? c.angle, real: by[c.id].camera || undefined } : c));
  const known = new Set(TEMPLE_CAMERAS.map(c => c.id));
  const extra: TempleCamera[] = saved.filter(p => !known.has(p.id) && !p.removed).map(p => ({
    id: p.id, name: 'Added position', te: '', x: p.x, y: p.y, angle: p.angle ?? 0, fov: 70, distanceM: 0, line: '', lineColor: '#38BDF8',
    sector: '', resolution: '', fps: 0, defaultDens: 0, kind: 'area', real: p.camera || undefined }));
  return [...base, ...extra];
};

export const MainTempleRoomViewInner: React.FC = () => {
  const { themeMode } = useCrowd();

  const isLight = themeMode === 'light';

  // Visualization settings & filters
  const [orientation, setOrientation] = useState<'horizontal' | 'vertical'>('horizontal');
  const [theme, setTheme] = useState<'architectural' | 'cyber'>(isLight ? 'architectural' : 'cyber');
  const [showCameras, setShowCameras] = useState<boolean>(true);
  const [showFovCones, setShowFovCones] = useState<boolean>(true);
  const [showDistances, setShowDistances] = useState<boolean>(false);
  const [hoveredCam, setHoveredCam] = useState<TempleCamera | null>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const panStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // live cameras (state, people in view) and the temple figures of the Overview, refreshed every 5 s
  const [live, setLive] = useState<Record<string, LiveCam>>({});
  const [kpis, setKpis] = useState<any>(null);
  const [view, setView] = useState<TempleCamera | null>(null);

  // The arrangement of the plan: the built-in positions, changed by what was saved from this page
  // (GET / PUT /api/templeroom/layout: place, viewing direction and camera of each position). The page opens on
  // the arrangement it showed last time in this browser; on a first visit it shows no positions until the saved
  // arrangement has come, so the built-in ones never flash up first.
  const [cams, setCams] = useState<TempleCamera[]>(() => {
    const last = readLast<SavedPos[]>(LAYOUT_KEY, Infinity);
    return last ? applySaved(last) : [];
  });
  // nothing is saved before the saved arrangement is known: an edit made earlier would overwrite it
  const layoutLoaded = useRef<boolean>(readLast(LAYOUT_KEY, Infinity) !== null);
  const [editing, setEditing] = useState<boolean>(false);
  const [selId, setSelId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const dragRef = useRef<{ id: string; moved: boolean } | null>(null);
  const camsRef = useRef<TempleCamera[]>(cams);
  camsRef.current = cams;

  useEffect(() => {
    let alive = true;
    baseapi.get('templeroom/layout')
      .then(r => {
        if (!alive) return;
        const saved = Array.isArray(r.data?.positions) ? r.data.positions : [];
        setCams(applySaved(saved)); saveLast(LAYOUT_KEY, saved); layoutLoaded.current = true;
      })
      // not reachable: keep the last known arrangement, or show the built-in one rather than an empty plan
      .catch(() => { if (alive) setCams(cur => (cur.length ? cur : TEMPLE_CAMERAS)); });
    return () => { alive = false; };
  }, []);

  const save = (list: TempleCamera[]) => {
    if (!layoutLoaded.current) { setSaveState('failed'); return; }
    const removed = TEMPLE_CAMERAS.filter(c => !list.some(l => l.id === c.id)).map(c => ({ id: c.id, x: c.x, y: c.y, angle: c.angle, removed: true }));
    const positions = [...list.map(c => ({ id: c.id, x: c.x, y: c.y, angle: c.angle, camera: c.real || null })), ...removed];
    setSaveState('saving');
    baseapi.put('templeroom/layout', { positions })
      .then(() => { setSaveState('saved'); saveLast(LAYOUT_KEY, positions); }).catch(() => setSaveState('failed'));
  };
  const change = (id: string, patch: Partial<TempleCamera>, persist = true) => {
    const next = camsRef.current.map(c => (c.id === id ? { ...c, ...patch } : c));
    setCams(next);
    if (persist) save(next);
  };
  const addPosition = () => {
    const n = Math.max(0, ...camsRef.current.map(c => parseInt(c.id.replace(/\D/g, ''), 10) || 0)) + 1;
    const id = `CAM-TR-${String(n).padStart(2, '0')}`;
    const next = [...camsRef.current, { id, name: 'Added position', te: '', x: 465, y: 640, angle: 90, fov: 70, distanceM: 0, line: '', lineColor: '#38BDF8',
      sector: '', resolution: '', fps: 0, defaultDens: 0, kind: 'area' as const }];
    setCams(next); setSelId(id); save(next);
  };
  const removePosition = (id: string) => { const next = camsRef.current.filter(c => c.id !== id); setCams(next); setSelId(null); save(next); };
  const resetLayout = () => { setCams(TEMPLE_CAMERAS); setSelId(null); save(TEMPLE_CAMERAS); };
  const sel = cams.find(c => c.id === selId) || null;
  // placing aid: with "Coordinates" on, the plan shows the position under the mouse and keeps the last clicked one,
  // so a camera's place can be read off and passed on (x, y in the plan's own 930 x 1279 units)
  const [showCoords, setShowCoords] = useState<boolean>(() => new URLSearchParams(window.location.search).has('coords'));
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const [picked, setPicked] = useState<{ x: number; y: number }[]>([]);
  const planRef = useRef<SVGGElement | null>(null);
  const planPoint = (e: React.MouseEvent): { x: number; y: number } | null => {
    const g = planRef.current;
    const svg = g?.ownerSVGElement;
    const m = g?.getScreenCTM();
    if (!g || !svg || !m) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const q = pt.matrixTransform(m.inverse());
    return { x: Math.round(q.x), y: Math.round(q.y) };
  };
  useEffect(() => {
    let alive = true;
    const load = () => {
      baseapi.get('cameras/overview').then(r => {
        if (alive && Array.isArray(r.data?.cameras)) setLive(Object.fromEntries(r.data.cameras.map((c: LiveCam) => [c.id, c])));
      }).catch(() => undefined);
      baseapi.get('overview').then(r => { if (alive && r.data?.kpis) setKpis(r.data.kpis); }).catch(() => undefined);
    };
    load();
    const t = window.setInterval(load, 5000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);

  const liveOf = (cam: TempleCamera): LiveCam | undefined => (cam.real ? live[cam.real] : undefined);
  /** Marker colour: green = counting, teal = online on demand, red = bound but not answering, grey = no camera here yet. */
  const colorOf = (cam: TempleCamera) => {
    if (!cam.real) return GREY;
    const st = liveOf(cam)?.state;
    return st === 'online' ? '#10B981' : st === 'standby' ? '#14B8A6' : st ? '#EF4444' : GREY;
  };
  const bound = cams.filter(c => c.real);
  const boundOnline = bound.filter(c => ['online', 'standby'].includes(liveOf(c)?.state || '')).length;
  const inView = bound.reduce((a, c) => a + (liveOf(c)?.state === 'online' ? (liveOf(c)?.tracks ?? 0) : 0), 0);
  const darshanPerMin = kpis?.total_15m != null ? Math.round((kpis.total_15m / 15) * 10) / 10 : null;

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    panStartRef.current = { x: e.clientX - panOffset.x, y: e.clientY - panOffset.y };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragRef.current) {                       // edit mode: the marker follows the mouse
      const q = planPoint(e);
      if (q) { dragRef.current.moved = true; change(dragRef.current.id, { x: Math.max(0, Math.min(930, q.x)), y: Math.max(0, Math.min(1279, q.y)) }, false); }
      return;
    }
    if (!isPanning) return;
    setPanOffset({
      x: e.clientX - panStartRef.current.x,
      y: e.clientY - panStartRef.current.y
    });
  };

  const handleMouseUp = () => {
    if (dragRef.current) {
      if (dragRef.current.moved) save(camsRef.current);
      dragRef.current = null;
    }
    setIsPanning(false);
  };

  const resetView = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleWheel = (e: React.WheelEvent) => {
    const zoomDelta = e.deltaY < 0 ? 0.08 : -0.08;
    setZoomLevel(z => Math.min(2.5, Math.max(0.4, Number((z + zoomDelta).toFixed(2)))));
  };

  // a bound camera opens its real video; a planned position has nothing to show
  const handleOpenCam = (cam: TempleCamera) => { if (editing) setSelId(cam.id); else if (cam.real) setView(cam); };

  return (
    <div className="w-full h-[calc(100vh-6.75rem)] max-h-[calc(100vh-6.75rem)] flex flex-col rounded-xl border border-neutral-200/80 dark:border-[#262b37] overflow-hidden bg-white dark:bg-[#181b22] text-neutral-900 dark:text-neutral-100 font-sans shadow-xs transition-colors duration-200">
      {/* Top Header & Telemetry Strip */}
      <div className="shrink-0 px-3.5 py-2 bg-white dark:bg-[#181b22] border-b border-neutral-200/80 dark:border-[#262b37] flex flex-wrap items-center justify-between gap-3 z-30 shadow-xs">
        {/* Title & Temple Info */}
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-sm sm:text-base font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
                Main Temple Room — Queue Layout &amp; Sanctum Lines
              </h1>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                Live Map
              </span>
            </div>
            <p className="text-[11px] sm:text-xs text-neutral-500 dark:text-neutral-400 font-normal mt-0.5">
              శ్రీ కనకదుర్గ అమ్మవారి మూలవిరాట్ గర్భాలయం · ఆశీర్వచన మండపం · 5 రంగుల క్యూ మార్గాలు (Temple Room Queue Map)
            </p>
          </div>
        </div>

        {/* Live figures: the same sources as the Overview tiles, plus this page's own cameras */}
        <div className="flex items-center gap-2.5 overflow-x-auto scrollbar-none py-0.5">
          <div title="Entered at the Antharalayam entry cameras minus exited at its exit cameras, today" className="px-3 py-1.5 rounded-lg bg-neutral-50/90 dark:bg-[#14171f] border border-neutral-200/60 dark:border-[#222733] flex items-center gap-2.5 shrink-0">
            <Users className="w-4 h-4 text-blue-500 shrink-0" />
            <div>
              <div className="text-[10.5px] sm:text-[11px] uppercase font-mono tracking-wider text-neutral-500 dark:text-neutral-400 font-semibold leading-tight">In Antharalayam</div>
              <div className="font-mono text-base sm:text-lg font-bold leading-tight text-blue-600 dark:text-blue-400">{fmt(kpis?.antharalayam)}</div>
            </div>
          </div>
          <div title="Counted in past the two queue line cameras (192.168.2.38 + 192.168.2.41) today" className="px-3 py-1.5 rounded-lg bg-neutral-50/90 dark:bg-[#14171f] border border-neutral-200/60 dark:border-[#222733] flex items-center gap-2.5 shrink-0">
            <Activity className="w-4 h-4 text-cyan-500 shrink-0" />
            <div>
              <div className="text-[10.5px] sm:text-[11px] uppercase font-mono tracking-wider text-neutral-500 dark:text-neutral-400 font-semibold leading-tight">Devotees today</div>
              <div className="font-mono text-base sm:text-lg font-bold leading-tight text-cyan-600 dark:text-cyan-400">{fmt(kpis?.total)}</div>
            </div>
          </div>
          <div title="Devotees through the queue lines per minute, last 15 minutes" className="px-3 py-1.5 rounded-lg bg-neutral-50/90 dark:bg-[#14171f] border border-neutral-200/60 dark:border-[#222733] flex items-center gap-2.5 shrink-0">
            <Zap className="w-4 h-4 text-amber-500 shrink-0" />
            <div>
              <div className="text-[10.5px] sm:text-[11px] uppercase font-mono tracking-wider text-neutral-500 dark:text-neutral-400 font-semibold leading-tight">Darshan rate</div>
              <div className="font-mono text-base sm:text-lg font-bold leading-tight text-amber-600 dark:text-amber-400">{darshanPerMin != null ? `${darshanPerMin}/min` : '—'}</div>
            </div>
          </div>
          <div title="People the cameras on this plan see at this moment" className="px-3 py-1.5 rounded-lg bg-neutral-50/90 dark:bg-[#14171f] border border-neutral-200/60 dark:border-[#222733] flex items-center gap-2.5 shrink-0">
            <Clock className="w-4 h-4 text-purple-500 shrink-0" />
            <div>
              <div className="text-[10.5px] sm:text-[11px] uppercase font-mono tracking-wider text-neutral-500 dark:text-neutral-400 font-semibold leading-tight">In view now</div>
              <div className="font-mono text-base sm:text-lg font-bold leading-tight text-purple-600 dark:text-purple-400">{fmt(inView)}</div>
            </div>
          </div>
          <div title="Cameras bound to this plan that are online; the other positions have no camera assigned yet" className="px-3 py-1.5 rounded-lg bg-neutral-50/90 dark:bg-[#14171f] border border-neutral-200/60 dark:border-[#222733] flex items-center gap-2.5 shrink-0">
            <Camera className="w-4 h-4 text-emerald-500 shrink-0" />
            <div>
              <div className="text-[10.5px] sm:text-[11px] uppercase font-mono tracking-wider text-neutral-500 dark:text-neutral-400 font-semibold leading-tight">Cameras</div>
              <div className="font-mono text-base sm:text-lg font-bold leading-tight text-emerald-600 dark:text-emerald-400">{boundOnline} / {bound.length} online</div>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Controls & Layer Bar */}
      <div className="shrink-0 px-3.5 py-1.5 bg-white dark:bg-[#181b22] border-b border-neutral-200/80 dark:border-[#262b37] flex flex-wrap items-center justify-between gap-2 z-20 text-xs">
        {/* Left: Toggles */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Orientation switcher */}
          <button
            onClick={() => setOrientation(o => o === 'horizontal' ? 'vertical' : 'horizontal')}
            className={`px-2.5 py-1 rounded-md font-mono text-xs font-semibold border transition cursor-pointer flex items-center gap-1.5 ${
              orientation === 'horizontal'
                ? 'bg-blue-50 dark:bg-blue-950/40 border-blue-300 dark:border-blue-800 text-blue-600 dark:text-blue-400'
                : 'bg-neutral-50 dark:bg-[#14171f] border-neutral-200/80 dark:border-[#262b37] text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100'
            }`}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Layout: {orientation === 'horizontal' ? '↔ Horizontal' : '↕ Vertical'}</span>
          </button>

          <button
            onClick={() => setTheme(t => t === 'architectural' ? 'cyber' : 'architectural')}
            className={`px-2.5 py-1 rounded-md font-mono text-xs font-semibold border transition cursor-pointer flex items-center gap-1.5 ${
              theme === 'architectural'
                ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800 text-amber-600 dark:text-amber-400'
                : 'bg-sky-50 dark:bg-sky-950/40 border-sky-300 dark:border-sky-800 text-sky-600 dark:text-sky-400'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Theme: {theme === 'architectural' ? 'Architectural' : 'Dark Cyber'}</span>
          </button>

          <span className="w-[1px] h-3.5 bg-neutral-200 dark:bg-[#262b37] mx-0.5" />

          <button
            onClick={() => setShowCameras(p => !p)}
            className={`px-2.5 py-1 rounded-md font-mono text-xs border transition flex items-center gap-1.5 cursor-pointer ${
              showCameras
                ? 'bg-blue-50 dark:bg-blue-950/40 border-blue-300 dark:border-blue-800 text-blue-600 dark:text-blue-400 font-bold'
                : 'opacity-60 border-neutral-200/80 dark:border-[#262b37] text-neutral-500 hover:opacity-100'
            }`}
          >
            <Camera className="w-3.5 h-3.5" />
            <span>CCTV ({bound.length} live · {cams.length - bound.length} planned)</span>
          </button>

          <button
            onClick={() => setShowFovCones(p => !p)}
            className={`px-2.5 py-1 rounded-md font-mono text-xs border transition flex items-center gap-1.5 cursor-pointer ${
              showFovCones
                ? 'bg-cyan-50 dark:bg-cyan-950/40 border-cyan-300 dark:border-cyan-800 text-cyan-600 dark:text-cyan-400 font-bold'
                : 'opacity-60 border-neutral-200/80 dark:border-[#262b37] text-neutral-500 hover:opacity-100'
            }`}
          >
            <Eye className="w-3.5 h-3.5" />
            <span>FOV Cones</span>
          </button>

          <button
            onClick={() => setShowDistances(p => !p)}
            className={`px-2.5 py-1 rounded-md font-mono text-xs border transition flex items-center gap-1.5 cursor-pointer ${
              showDistances
                ? 'bg-purple-50 dark:bg-purple-950/40 border-purple-300 dark:border-purple-800 text-purple-600 dark:text-purple-400 font-bold'
                : 'opacity-60 border-neutral-200/80 dark:border-[#262b37] text-neutral-500 hover:opacity-100'
            }`}
          >
            <Sliders className="w-3.5 h-3.5" />
            <span>Distance Tags</span>
          </button>

          <button
            onClick={() => { setEditing(p => !p); setSelId(null); setSaveState('idle'); }}
            title="Move the camera markers and choose which camera is at each position"
            className={`px-2.5 py-1 rounded-md font-mono text-xs border transition flex items-center gap-1.5 cursor-pointer ${
              editing
                ? 'bg-orange-500 border-orange-500 text-white font-bold'
                : 'bg-neutral-50 dark:bg-[#14171f] border-neutral-200/80 dark:border-[#262b37] text-neutral-700 dark:text-neutral-300 hover:text-orange-600'
            }`}
          >
            <Pencil className="w-3.5 h-3.5" />
            <span>{editing ? 'Done editing' : 'Edit cameras'}</span>
          </button>

          <button
            onClick={() => setShowCoords(p => !p)}
            title="Show plan coordinates under the mouse; double-click the plan to mark a point"
            className={`px-2.5 py-1 rounded-md font-mono text-xs border transition flex items-center gap-1.5 cursor-pointer ${
              showCoords
                ? 'bg-orange-50 dark:bg-orange-950/40 border-orange-300 dark:border-orange-800 text-orange-600 dark:text-orange-400 font-bold'
                : 'opacity-60 border-neutral-200/80 dark:border-[#262b37] text-neutral-500 hover:opacity-100'
            }`}
          >
            <span>Coordinates</span>
          </button>
        </div>

        {/* Right: Zoom & Pan */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10.5px] font-mono text-neutral-500 dark:text-neutral-400">
            {Math.round(zoomLevel * 100)}%
          </span>
          <button
            onClick={() => setZoomLevel(z => Math.min(2.5, Number((z + 0.1).toFixed(2))))}
            className="p-1 rounded-md border bg-neutral-50 dark:bg-[#14171f] border-neutral-200/80 dark:border-[#262b37] text-neutral-600 dark:text-neutral-400 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-800 transition cursor-pointer"
            title="Zoom In"
          >
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setZoomLevel(z => Math.max(0.35, Number((z - 0.1).toFixed(2))))}
            className="p-1 rounded-md border bg-neutral-50 dark:bg-[#14171f] border-neutral-200/80 dark:border-[#262b37] text-neutral-600 dark:text-neutral-400 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-800 transition cursor-pointer"
            title="Zoom Out"
          >
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={resetView}
            className="p-1 rounded-md border bg-neutral-50 dark:bg-[#14171f] border-neutral-200/80 dark:border-[#262b37] text-neutral-600 dark:text-neutral-400 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-800 transition cursor-pointer"
            title="Reset to Fit"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Main SVG Container */}
      <div
        className="flex-1 min-h-0 relative overflow-hidden cursor-grab active:cursor-grabbing flex items-center justify-center p-2 bg-[#f8f9fc] dark:bg-[#090b10]"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <div
          className="w-full h-full flex items-center justify-center transition-transform duration-75"
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
            transformOrigin: 'center center'
          }}
        >
          <svg
            viewBox={orientation === 'horizontal' ? '0 0 1279 930' : '0 0 930 1279'}
            className="w-full h-full block max-h-full max-w-full drop-shadow-xl"
            preserveAspectRatio="xMidYMid meet"
            style={{
              filter: theme === 'cyber' && !isLight
                ? 'drop-shadow(0 0 35px rgba(0, 240, 181, 0.08))'
                : 'drop-shadow(0 4px 20px rgba(0,0,0,0.25))'
            }}
          >
            <defs>
              {/* Glowing filters */}
              <filter id="templeGlowGold" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>

            {/* Rotatable Blueprint & Overlay Container */}
            <g ref={planRef} transform={orientation === 'horizontal' ? 'translate(1279, 0) rotate(90)' : undefined}
              onMouseMove={showCoords ? e => setMousePos(planPoint(e)) : undefined}
              onMouseLeave={showCoords ? () => setMousePos(null) : undefined}
              onDoubleClick={showCoords ? e => { const q = planPoint(e); if (q) setPicked(l => [...l, q]); } : undefined}>
              {/* Base Replicated Map Image */}
              <image
                href="/temple6.webp"
                x="0"
                y="0"
                width="930"
                height="1279"
                preserveAspectRatio="none"
                style={{
                  filter: theme === 'cyber' && !isLight
                    ? 'invert(90%) hue-rotate(180deg) brightness(0.75) contrast(1.2)'
                    : isLight
                      ? 'contrast(1.05) brightness(1.02)'
                      : 'none'
                }}
              />

              {/* picked points of the placing aid, numbered in the order they were double-clicked */}
              {showCoords && picked.map((q, i) => (
                <g key={`pick-${i}`} className="pointer-events-none">
                  <circle cx={q.x} cy={q.y} r="7" fill="#F97316" stroke="#fff" strokeWidth="1.5" />
                  <text x={q.x} y={q.y + 3.5} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold"
                    transform={orientation === 'horizontal' ? `rotate(-90 ${q.x} ${q.y})` : undefined}>{i + 1}</text>
                </g>
              ))}

              {/* ================= DISTANCE SPACING MARKERS ================= */}
              {showDistances && (
                <g className="pointer-events-none">
                  {[
                    { x: 197, y: 750, label: '15m' },
                    { x: 197, y: 550, label: '30m' },
                    { x: 197, y: 350, label: '45m' },
                    { x: 260, y: 118, label: '60m' },
                    { x: 420, y: 118, label: '75m' },
                    { x: 708, y: 200, label: '120m' },
                    { x: 708, y: 400, label: '150m' },
                    { x: 600, y: 500, label: '180m' },
                    { x: 708, y: 600, label: '210m' }
                  ].map((dm, idx) => (
                    <g key={`temple-dist-${idx}`}>
                      <circle cx={dm.x} cy={dm.y} r="3.5" fill="#38BDF8" stroke="#0F172A" strokeWidth="1" />
                      <rect x={dm.x - 16} y={dm.y - 10} width="32" height="18" rx="2" fill="rgba(15, 23, 42, 0.9)" stroke="#38BDF8" strokeWidth="0.8" />
                      <text x={dm.x} y={dm.y + 3.5} textAnchor="middle" fill="#38BDF8" fontFamily="var(--font-mono, monospace)" fontSize="10" fontWeight="bold">
                        {dm.label}
                      </text>
                    </g>
                  ))}
                </g>
              )}

              {/* ================= CAMERA FOV VIEW CONES ================= */}
              {showCameras && showFovCones && (
                <g className="pointer-events-none">
                  {cams.map(cam => {
                    const rad = (cam.angle * Math.PI) / 180;
                    const spreadRad = ((cam.fov / 2) * Math.PI) / 180;
                    const length = 75;
                    const x1 = cam.x + length * Math.cos(rad - spreadRad);
                    const y1 = cam.y + length * Math.sin(rad - spreadRad);
                    const x2 = cam.x + length * Math.cos(rad + spreadRad);
                    const y2 = cam.y + length * Math.sin(rad + spreadRad);

                    const isHovered = hoveredCam?.id === cam.id;
                    if (!cam.real) return null;                 // a planned position has no view to draw

                    return (
                      <g key={`fov-${cam.id}`}>
                        <path
                          d={`M ${cam.x} ${cam.y} L ${x1} ${y1} A ${length} ${length} 0 0 1 ${x2} ${y2} Z`}
                          fill={isHovered ? 'rgba(56, 189, 248, 0.4)' : 'rgba(56, 189, 248, 0.15)'}
                          stroke={isHovered ? '#38BDF8' : 'rgba(56, 189, 248, 0.5)'}
                          strokeWidth={isHovered ? 1.5 : 0.8}
                        />
                      </g>
                    );
                  })}
                </g>
              )}

              {/* ================= CCTV CAMERAS (CLICKABLE & SENSING) ================= */}
              {showCameras && (
                <g>
                  {cams.map(cam => {
                    const isHovered = hoveredCam?.id === cam.id;
                    const statusCol = colorOf(cam);
                    const lc = liveOf(cam);

                    return (
                      <g
                        key={`cam-${cam.id}`}
                        opacity={cam.real || editing ? 1 : 0.55}
                        className={`${cam.real ? 'cursor-pointer' : 'cursor-default'} group`}
                        onMouseEnter={() => setHoveredCam(cam)}
                        onMouseLeave={() => setHoveredCam(null)}
                        onMouseDown={editing ? e => { e.stopPropagation(); dragRef.current = { id: cam.id, moved: false }; setSelId(cam.id); } : undefined}
                        onClick={() => handleOpenCam(cam)}
                        style={editing ? { cursor: 'move' } : undefined}
                      >
                        {editing && selId === cam.id && <circle cx={cam.x} cy={cam.y} r="19" fill="none" stroke="#F97316" strokeWidth="2" strokeDasharray="4 3" />}
                        <circle
                          cx={cam.x}
                          cy={cam.y}
                          r={isHovered ? '14' : '10'}
                          fill="#0F172A"
                          stroke={statusCol}
                          strokeWidth={isHovered ? '2.5' : '1.8'}
                          className="transition-all duration-150"
                          style={{ filter: isHovered ? `drop-shadow(0 0 8px ${statusCol})` : 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))' }}
                        />

                        <g transform={`rotate(${cam.angle} ${cam.x} ${cam.y})`}>
                          <polygon
                            points={`${cam.x + 5},${cam.y} ${cam.x + 11},${cam.y - 3} ${cam.x + 11},${cam.y + 3}`}
                            fill={statusCol}
                          />
                        </g>

                        {/* Lens Graphic */}
                        <rect x={cam.x - 3.5} y={cam.y - 2.5} width="5" height="5" rx="1" fill={statusCol} />
                        <circle cx={cam.x - 1} cy={cam.y} r="1" fill="#0F172A" />

                        {/* blinking LED only while the camera is really delivering pictures */}
                        {lc?.state === 'online' && (
                          <circle cx={cam.x - 4.5} cy={cam.y - 4.5} r="1.5" fill="#EF4444">
                            <animate attributeName="opacity" values="1;0.2;1" dur="1.2s" repeatCount="indefinite" />
                          </circle>
                        )}

                        {/* Label badge */}
                        <rect
                          x={cam.x - 22}
                          y={cam.y + 11}
                          width="44"
                          height="13"
                          rx="2"
                          fill="rgba(15, 23, 42, 0.92)"
                          stroke={statusCol}
                          strokeWidth="0.8"
                        />
                        <text
                          x={cam.x}
                          y={cam.y + 20.5}
                          textAnchor="middle"
                          fill={statusCol}
                          fontFamily="var(--font-mono, monospace)"
                          fontSize="9"
                          fontWeight="bold"
                          style={{ pointerEvents: 'none' }}
                        >
                          {lc?.ip ? lc.ip.split('.').slice(2).join('.') : cam.id.replace('CAM-TR-', 'C-')}
                        </text>
                      </g>
                    );
                  })}
                </g>
              )}
            </g>
          </svg>
        </div>

        {/* edit panel: drag a marker on the plan, then choose its camera here */}
        {editing && (
          <div className="absolute top-3 right-3 z-40 w-80 bg-white/97 dark:bg-[#181b22]/97 border border-orange-300 dark:border-orange-800 rounded-xl shadow-xl p-3 text-[11.5px] text-neutral-800 dark:text-neutral-100"
            onMouseDown={e => e.stopPropagation()} onWheel={e => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2 pb-1.5 border-b border-neutral-200 dark:border-[#262b37]">
              <span className="font-bold text-orange-600 dark:text-orange-400">Editing camera positions</span>
              <span className={`text-[10px] font-mono ${saveState === 'failed' ? 'text-rose-500' : 'text-neutral-400'}`}>
                {saveState === 'saving' ? 'saving…' : saveState === 'saved' ? 'saved' : saveState === 'failed' ? 'NOT saved' : ''}
              </span>
            </div>
            <p className="mt-1.5 text-[10.5px] text-neutral-500 dark:text-neutral-400">Drag a marker to move it. Click a marker to choose its camera. Changes are saved for everybody.</p>
            {saveState === 'failed' && <p className="mt-1 text-[10.5px] text-rose-500 font-semibold">The server did not accept the change (open the console from the control-room network to edit).</p>}

            {sel ? (
              <div className="mt-2 space-y-2">
                <div className="flex items-center justify-between font-mono text-[11px]">
                  <b>{sel.id}</b><span className="text-neutral-400">x {sel.x}, y {sel.y}</span>
                </div>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wide text-neutral-400 font-semibold">Camera at this position</span>
                  <select value={sel.real || ''} onChange={e => change(sel.id, { real: e.target.value || undefined })}
                    className="mt-0.5 w-full bg-neutral-50 dark:bg-[#14171f] border border-neutral-300 dark:border-[#2b3140] rounded-md px-2 py-1 text-[11.5px] cursor-pointer">
                    <option value="">— no camera —</option>
                    {Object.values(live).filter(c => c.ip).sort((a, b) => (a.ip || '').localeCompare(b.ip || '', undefined, { numeric: true })).map(c => {
                      const usedBy = cams.find(x => x.real === c.id && x.id !== sel.id);
                      return <option key={c.id} value={c.id}>{c.ip} · {c.name}{usedBy ? ` (on ${usedBy.id})` : ''}</option>;
                    })}
                  </select>
                </label>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => change(sel.id, { angle: (sel.angle + 45) % 360 })} title="Turn the viewing direction by 45°"
                    className="px-2 py-1 rounded-md border border-neutral-300 dark:border-[#2b3140] hover:border-orange-400 flex items-center gap-1 cursor-pointer"><RotateCw className="w-3 h-3" /> Turn view</button>
                  <button onClick={() => removePosition(sel.id)} title="Take this position off the plan"
                    className="px-2 py-1 rounded-md border border-rose-300 dark:border-rose-800 text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 flex items-center gap-1 cursor-pointer"><Trash2 className="w-3 h-3" /> Remove</button>
                </div>
              </div>
            ) : (
              <p className="mt-2 text-[11px] text-neutral-500 dark:text-neutral-400">No marker selected.</p>
            )}

            <div className="mt-2.5 pt-2 border-t border-neutral-200 dark:border-[#262b37] flex items-center justify-between">
              <button onClick={addPosition} className="px-2 py-1 rounded-md bg-orange-500 text-white font-semibold flex items-center gap-1 cursor-pointer hover:bg-orange-600"><Plus className="w-3 h-3" /> Add position</button>
              <button onClick={resetLayout} title="Back to the built-in arrangement" className="text-[10.5px] text-neutral-500 underline cursor-pointer hover:text-rose-500">Reset all</button>
            </div>
          </div>
        )}

        {/* placing aid read-out */}
        {showCoords && (
          <div className="absolute bottom-3 left-3 z-40 max-w-md bg-white/95 dark:bg-[#181b22]/95 border border-orange-300 dark:border-orange-800 rounded-lg shadow-lg px-3 py-2 text-[11px] font-mono text-neutral-800 dark:text-neutral-100"
            onMouseDown={e => e.stopPropagation()}>
            <div><b className="text-orange-600 dark:text-orange-400">Mouse:</b> {mousePos ? `x ${mousePos.x}, y ${mousePos.y}` : 'move over the plan'}</div>
            <div className="text-neutral-500 dark:text-neutral-400">Double-click the plan to mark a camera position.</div>
            {picked.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 select-text">
                {picked.map((q, i) => <span key={i}><b>{i + 1}:</b> {q.x},{q.y}</span>)}
                <button onClick={() => setPicked([])} className="text-rose-500 underline cursor-pointer">clear</button>
              </div>
            )}
          </div>
        )}

        {/* Hovered camera card: real name, address, state and counts for a bound camera */}
        {hoveredCam && !editing && (() => {
          const lc = liveOf(hoveredCam);
          const col = colorOf(hoveredCam);
          const stateText = !hoveredCam.real ? 'No camera assigned' : lc?.state === 'online' ? 'Live' : lc?.state === 'standby' ? 'Online · on demand' : lc ? 'Offline' : 'Loading…';
          return (
            <div className="absolute top-4 right-4 z-40 w-72 bg-white/95 dark:bg-[#181b22]/95 border border-neutral-200/80 dark:border-[#262b37] rounded-xl shadow-xl p-3 backdrop-blur-md text-neutral-900 dark:text-neutral-100 pointer-events-none animate-in fade-in zoom-in-95 duration-150">
              <div className="flex items-center justify-between gap-2 pb-2 border-b border-neutral-100 dark:border-[#222733]">
                <div className="flex items-center gap-1.5 font-mono text-xs font-bold text-blue-600 dark:text-blue-400">
                  <Video className="w-3.5 h-3.5" />
                  {lc?.ip ?? hoveredCam.id}
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-mono font-bold border" style={{ color: col, borderColor: col }}>{stateText}</span>
              </div>
              <div className="py-2">
                <div className="text-xs font-bold text-neutral-900 dark:text-neutral-100">{lc?.name ?? hoveredCam.name}</div>
                <div className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5">{hoveredCam.real ? `${hoveredCam.real} · position ${hoveredCam.id}` : `Planned position ${hoveredCam.id}`}</div>
              </div>
              {hoveredCam.real ? (
                <div className="grid grid-cols-2 gap-1.5 pt-1 text-[10px] font-mono">
                  <div className="p-1.5 rounded-lg bg-neutral-50 dark:bg-[#14171f] border border-neutral-200/60 dark:border-[#222733]">
                    <div className="text-neutral-400 text-[8.5px] uppercase">In view now</div>
                    <div className="text-blue-600 dark:text-blue-400 font-bold text-xs">{lc?.state === 'online' ? fmt(lc.tracks ?? 0) : '—'}</div>
                  </div>
                  <div className="p-1.5 rounded-lg bg-neutral-50 dark:bg-[#14171f] border border-neutral-200/60 dark:border-[#222733]">
                    <div className="text-neutral-400 text-[8.5px] uppercase">Counted today</div>
                    <div className="text-amber-500 font-bold text-xs">{lc?.today ? fmt(Math.max(lc.today.people_toward, lc.today.people_away)) : '—'}</div>
                  </div>
                </div>
              ) : (
                <div className="text-[10.5px] text-neutral-500 dark:text-neutral-400">No figures: a camera has not been assigned to this position.</div>
              )}
              {hoveredCam.real && (
                <div className="mt-2 text-[9.5px] text-neutral-400 flex items-center justify-between border-t border-neutral-100 dark:border-[#222733] pt-1.5">
                  <span>Click the camera to open its video</span>
                  <span className="text-blue-600 dark:text-blue-400 font-bold">CLICK TO VIEW ▶</span>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Bottom strip: the bound cameras first (real name, address, people in view), then the planned positions */}
      <div className="shrink-0 border-t border-neutral-200/80 dark:border-[#262b37] bg-white dark:bg-[#181b22] p-2 z-20 flex flex-col gap-1.5 shadow-xs">
        <div className="flex items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-1.5 font-mono uppercase tracking-wider font-bold text-neutral-800 dark:text-neutral-200">
            <Video className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-xs">Temple cameras ({bound.length} live · {cams.length - bound.length} positions without a camera)</span>
          </div>
          <span className="text-[10px] text-neutral-400 font-mono">Click a live camera to open its video</span>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin">
          {[...bound, ...cams.filter(c => !c.real)].map(cam => {
            const lc = liveOf(cam);
            const statusCol = colorOf(cam);
            const isHovered = hoveredCam?.id === cam.id;
            return (
              <div
                key={`matrix-cam-${cam.id}`}
                onClick={() => handleOpenCam(cam)}
                onMouseEnter={() => setHoveredCam(cam)}
                onMouseLeave={() => setHoveredCam(null)}
                className={`shrink-0 w-44 p-1.5 rounded-lg border transition group ${cam.real ? 'cursor-pointer active:scale-95' : 'opacity-60'} ${
                  isHovered
                    ? 'bg-blue-50/80 dark:bg-blue-950/30 border-blue-500/60 shadow-xs'
                    : 'bg-neutral-50/80 dark:bg-[#14171f] border-neutral-200/60 dark:border-[#222733] hover:border-neutral-300 dark:hover:border-[#2a3040]'
                }`}
              >
                <div className="flex items-center justify-between gap-1 mb-0.5">
                  <span className="font-mono text-xs font-bold text-neutral-800 dark:text-neutral-200 flex items-center gap-1.5 truncate">
                    <Video className="w-3.5 h-3.5 text-blue-500" />
                    {lc?.ip ?? cam.id}
                  </span>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusCol }} />
                </div>
                <div className="text-[11px] sm:text-xs font-medium text-neutral-600 dark:text-neutral-300 truncate mb-0.5" title={lc?.name ?? cam.name}>{lc?.name ?? cam.name}</div>
                <div className="flex items-center justify-between text-[10px] font-mono text-neutral-400">
                  <span>{cam.real ? cam.id : 'no camera assigned'}</span>
                  {cam.real && <span style={{ color: statusCol }} className="font-bold">{lc?.state === 'online' ? `${lc.tracks ?? 0} in view` : lc?.state === 'standby' ? 'on demand' : lc ? 'offline' : '…'}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* real video of the clicked camera */}
      {view && view.real && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setView(null)}>
          <div className="w-[1100px] max-w-full bg-[#0f1218] border border-[#262b37] rounded-xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-3 py-2 border-b border-[#262b37] text-neutral-100">
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: colorOf(view) }} />
              <span className="text-[13px] font-bold truncate">{liveOf(view)?.name ?? view.name}</span>
              <span className="text-[11px] text-neutral-400 font-mono">{liveOf(view)?.ip ?? ''} · {view.real}</span>
              <button onClick={() => setView(null)} className="ml-auto p-1 rounded hover:bg-white/10 cursor-pointer" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            <div className="aspect-video bg-black">
              <LiveFrame src={`${API_BASE}cameras/${encodeURIComponent(view.real)}/snapshot.jpg`} alt={view.real} size="full" maxFps={12} className="w-full h-full object-contain" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const MainTempleRoomView: React.FC = () => {
  return (
    <CrowdProvider>
      <MainTempleRoomViewInner />
    </CrowdProvider>
  );
};

